import { useMemo } from 'react';
import { useScorecardData } from 'hooks';
import { useSelector } from 'react-redux';
import { useScorecardContext } from 'contexts';
import { selectData } from 'store/scorecards';
import { ScorecardGoal, ScorecardSubGoal } from 'store/interfaces';
import { SCORECARD_GOAL_TYPE_METRIC } from 'constants/scorecards';
import { AssessmentScaleGroupAlias } from 'store/interfaces/ScorecardExternalSubgoalRO';
import { hasOnlyNaOrDontKnowFeedback, getSubgoalFeedbacks } from 'helpers';

interface UseHasOnlyNAFeedbackResult {
  hasOnlyNAFeedback: boolean;
  goalNames: string[];
}

interface ProcessedGoal {
  id: number;
  name: string;
  weight: number;
  type?: string;
  subgoals: ScorecardSubGoal[];
}

interface ProcessedFeedback {
  feedbackGoalHeader: {
    assessmentScoreFinalized?: { name: string; alias?: string } | null;
    assessmentScoreOriginal?: { name: string; alias?: string } | null;
  };
  isSelfAssessment: boolean;
}

export default function useHasOnlyNAFeedback(
  goalId?: number,
  subgoalId?: number,
): UseHasOnlyNAFeedbackResult {
  const { feedbacks: scorecardFeedbacks, workRecord } = useScorecardData();
  const { id: scorecardId, store: scorecardStore } = useScorecardContext();
  const { data = null } =
    useSelector(selectData(scorecardStore, scorecardId)) || {};

  return useMemo(() => {
    // The warning is not relevant on the "My Scorecards" (home) page — the
    // owner only views their scorecards there and can't adjust subgoal weights.
    if (scorecardStore === 'scorecardsOwn') {
      return { hasOnlyNAFeedback: false, goalNames: [] };
    }

    if (!data?.goals || !scorecardFeedbacks) {
      return { hasOnlyNAFeedback: false, goalNames: [] };
    }

    let goalsToProcess: ProcessedGoal[] = [];
    if (goalId && subgoalId) {
      const goal = data.goals?.find((g: ScorecardGoal) => g.id === goalId);
      if (goal && goal.weight > 0 && goal.type !== SCORECARD_GOAL_TYPE_METRIC) {
        const subgoal = goal?.subgoals?.find(
          (s: ScorecardSubGoal) => s.id === subgoalId,
        );
        if (subgoal && subgoal.weight > 0) {
          goalsToProcess = [{ ...goal, subgoals: [subgoal] }];
        }
      }
    } else {
      goalsToProcess = data.goals || [];
    }

    const matchingGoals: string[] = [];

    goalsToProcess?.forEach((goal: ProcessedGoal) => {
      // A goal with weight 0 does not impact the Total Score, and the warning
      // is only relevant for regular goals (not metric goals), so its subgoals
      // never need the "change weight to 0" warning in those cases.
      if (goal.weight === 0 || goal.type === SCORECARD_GOAL_TYPE_METRIC) {
        return;
      }

      let hasOnlyNAFeedbackInGoal = false;

      if (goal?.subgoals && Array.isArray(goal?.subgoals)) {
        goal?.subgoals?.forEach((subgoal: ScorecardSubGoal) => {
          if (subgoal.weight === 0) {
            return;
          }

          if (
            subgoal.assessmentScaleGroup?.alias ===
            AssessmentScaleGroupAlias.TEXT
          ) {
            return;
          }

          const subgoalFeedbacks: ProcessedFeedback[] = scorecardFeedbacks
            ? getSubgoalFeedbacks(
                scorecardFeedbacks,
                goal.id,
                subgoal.id,
                workRecord?.user?.id || undefined,
              )
            : [];

          const hasOnlyNAFeedback =
            hasOnlyNaOrDontKnowFeedback(subgoalFeedbacks);

          if (hasOnlyNAFeedback) {
            hasOnlyNAFeedbackInGoal = true;
          }
        });
      }

      if (hasOnlyNAFeedbackInGoal) {
        matchingGoals.push(goal.name);
      }
    });

    return {
      hasOnlyNAFeedback: matchingGoals.length > 0,
      goalNames: matchingGoals,
    };
  }, [
    data?.goals,
    scorecardFeedbacks,
    workRecord?.user?.id,
    goalId,
    subgoalId,
    scorecardStore,
  ]);
}
